import React from 'react';
import { useMessage } from '@tmapy/intl';

import '../../app/components/Badge.scss';

import { SvgSpinner, Icon, InfoBadge, SuccessBadge, DangerBadge, Badge } from '@tmapy/style-guide';

import { DOC_STATE } from '../constants';
import { formatDocState } from '../utils/formatDocState';
import { FlowEnum } from '../../openapi/types/FlowEnum';
import type { NestedSubjektResponse } from '../../openapi/types/NestedSubjekt';

type DocBadgeProps = {
  docState: DOC_STATE;
  flow?: FlowEnum | '' | null;
  rejectedBySubjekty?: readonly NestedSubjektResponse[];
};

export const DocBadge: React.FC<DocBadgeProps> = ({ docState, flow, rejectedBySubjekty }) => {
  const formatMessage = useMessage();
  const stateText = formatDocState(formatMessage, docState, flow, rejectedBySubjekty);

  switch (docState) {
    // INFO
    case DOC_STATE.INPROGRESS: {
      return <InfoBadge>{stateText}</InfoBadge>;
    }
    // INFO STRONG
    case DOC_STATE.DRAFT_CHECKED: {
      return <InfoBadge isStrong>{stateText}</InfoBadge>;
    }
    // SUCCESS
    case DOC_STATE.CLOSED:
    case DOC_STATE.CLOSED_FINAL:
    case DOC_STATE.CLOSING_READY_TO_SEND_TO_DMVS:
    case DOC_STATE.DRAFT:
    case DOC_STATE.IMPORTED:
    case DOC_STATE.NOTIFYING_CLOSED:
    case DOC_STATE.PREPARING_FOR_EDIT:
    case DOC_STATE.PROCESSED:
    case DOC_STATE.READY_TO_CHECK_SVU_CROSS_KRAJ:
    case DOC_STATE.READY_TO_IMPORT:
    case DOC_STATE.READY_TO_PROCESS:
    case DOC_STATE.READY_TO_SEND_TO_DMVS:
    case DOC_STATE.READY_TO_SYNCHRONIZE:
    case DOC_STATE.SENT_TO_DMVS:
    case DOC_STATE.SUBMITTED:
    case DOC_STATE.SYNCHRONIZED:
    case DOC_STATE.SIGNING:
    case DOC_STATE.SIGNED:
    case DOC_STATE.TAKEN_OVER: {
      return <SuccessBadge>{stateText}</SuccessBadge>;
    }
    // SUCCESS STRONG
    case DOC_STATE.ACCEPTED:
    case DOC_STATE.CLOSING_FINAL:
    case DOC_STATE.WAITING_FOR_CLOSE_FINAL: {
      return <SuccessBadge isStrong>{stateText}</SuccessBadge>;
    }
    // DANGER
    case DOC_STATE.CLOSE_CHECKS_FAILED:
    case DOC_STATE.CLOSE_FAILED:
    case DOC_STATE.CLOSE_FINAL_FAILED:
    case DOC_STATE.CLOSE_INTERRUPTED:
    case DOC_STATE.DELEGATE_FAILED:
    case DOC_STATE.DMVS_COMMUNICATION_FAILED:
    case DOC_STATE.EDIT_SUPERVISING:
    case DOC_STATE.FAILED_TO_GET_SYNCHRONIZE_DATA:
    case DOC_STATE.FAILED_TO_SEND_SYNCHRONIZE_DATA:
    case DOC_STATE.IMPORT_FAILED:
    case DOC_STATE.IMPORT_INTERRUPTED:
    case DOC_STATE.IMPORTED_WITH_ERRORS:
    case DOC_STATE.NOTIFYING_CLOSE_FAILED:
    case DOC_STATE.PREPARING_FOR_EDIT_WITH_ERRORS:
    case DOC_STATE.CHECK_SVU_CROSS_KRAJ_FAILED:
    case DOC_STATE.PROCESS_FAILED:
    case DOC_STATE.REJECTED:
    case DOC_STATE.REJECTED_BY_COUNTERPART:
    case DOC_STATE.SEND_TO_COUNTERPART_FAILED:
    case DOC_STATE.SEND_TO_DMVS_FAILED:
    case DOC_STATE.SIGNED_FAILED:
    case DOC_STATE.SYNCHRONIZE_FAILED:
    case DOC_STATE.WAITING_FOR_REJECT_FAILED: {
      return <DangerBadge>{stateText}</DangerBadge>;
    }
    // DANGER STRONG
    case DOC_STATE.CANCELED:
    case DOC_STATE.EDIT_TAKEOVER_REJECTED:
    case DOC_STATE.EDIT_REJECTED: {
      return <DangerBadge isStrong>{stateText}</DangerBadge>;
    }
    // INPROGRESS
    case DOC_STATE.CLOSING:
    case DOC_STATE.EDITING:
    case DOC_STATE.IMPORTING:
    case DOC_STATE.PROCESSING:
    case DOC_STATE.SYNCHRONIZING:
    case DOC_STATE.WAITING_FOR_SYNCHRONIZE_DATA:
    case DOC_STATE.SENT_TO_COUNTERPART:
    case DOC_STATE.PROCESSED_SENT_TO_COUNTERPART:
    case DOC_STATE.DELEGATED:
    case DOC_STATE.DELEGATE_REJECTED:
    case DOC_STATE.WAITING_FOR_CLOSE:
    case DOC_STATE.WAITING_FOR_IMPORT:
    case DOC_STATE.WAITING_FOR_REJECT:
    case DOC_STATE.WAITING_FOR_PROCESS:
    case DOC_STATE.WAITING_FOR_SYNCHRONIZE:
    case DOC_STATE.WAITING_FOR_CHECK_SVU_CROSS_KRAJ:
    case DOC_STATE.CHECKING_SVU_CROSS_KRAJ:
    case DOC_STATE.NOTIFYING_CLOSE:
    case DOC_STATE.SENDING_TO_DMVS: {
      return (
        <Badge unsafeClassName='dmx-badge--in-progress'>
          <div className='sg-a-d-f sg-a-ai-c'>
            <Icon element={<SvgSpinner />} />
            <span className='sg-a-ml-1/2 sg-a-ws-nw'>{stateText}</span>
          </div>
        </Badge>
      );
    }
    // DEFAULT
    default: {
      console.warn(`[DocBadge]: Unknown docState (${docState}) in flow (${flow})`);
      return docState;
    }
  }
};